import { useToast } from '@chakra-ui/react'
import { faUserSlash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import FormButton from '../../../../../components/FormButton'
import PageWrapper from '../../../../../components/Pagewrapper'
import Toast from '../../../../../components/Toast'
import {
  coreApi,
  useDeclareKycResidenceMutation,
  useGetUserQuery,
} from '../../../../../redux/services/coreApi'
import type { RootState } from '../../../../../redux/store'
import KycResidenceModal from './KycResidenceModal'

type CancelledProps = {}

const Cancelled: React.FC<CancelledProps> = ({}) => {
  const session = useSelector((state: RootState) => state.session)
  const history = useHistory()
  const dispatch = useDispatch()
  const toast = useToast()
  const user = useSelector((state: RootState) => state.session.user)
  const { data: userData } = useGetUserQuery(user.id)
  const [declareKycResidence, { isLoading: isDeclaringResidence }] =
    useDeclareKycResidenceMutation()
  const [isResidenceModalOpen, setIsResidenceModalOpen] = useState(false)

  const kycDetails = userData?.kycDetails
  const isRetry = kycDetails?.sumsubReviewRejectType === 'RETRY'
  const rejectionReason =
    kycDetails?.sumsubReviewModerationComment || 'unspecified reason'

  // A retry re-opens the WebSDK through the same door as a fresh Start, and that door runs the pre-KYC
  // country gate (ADR-0015): the access token is refused for an individual with no declared residence. A
  // customer who was asked to resubmit before the gate existed has none committed yet, so collect it here
  // rather than send them into a verification screen that can't open.
  const needsResidence =
    userData?.role === 'Individual' && !userData?.declaredResidenceIso

  const handleTryAgain = () => {
    if (needsResidence) {
      setIsResidenceModalOpen(true)
      return
    }
    history.push('/app/kyc/sumsub')
  }

  const handleSubmitResidence = async (
    residenceIso: string
  ): Promise<'eligible' | 'blocked' | 'error'> => {
    try {
      const { eligible } = await declareKycResidence({ residenceIso }).unwrap()
      if (!eligible) return 'blocked'
      setIsResidenceModalOpen(false)
      dispatch(coreApi.util.invalidateTags(['User']))
      history.push('/app/kyc/sumsub')
      return 'eligible'
    } catch (error) {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">
            We couldn't check your country of residence. Please try again.
          </Toast>
        ),
      })
      return 'error'
    }
  }

  return (
    <PageWrapper fixedWidth>
      <div className="card gap-2 mt-20 flex flex-col text-center items-center">
        <FontAwesomeIcon
          icon={faUserSlash}
          className="h-9 w-9 mt-5 text-brand-light-red"
        />
        <h1 className="font-semi text-lg">
          {isRetry ? 'Verification Needs Attention' : 'Verification Declined'}
        </h1>
        <span className="text-description w-2/3 mb-5 mt-5">
          {isRetry ? (
            <>
              Your verification needs some adjustments. Here's what needs to be
              fixed:
              <div className="mt-4 p-4 bg-brand-gray-50 rounded-lg text-left">
                {rejectionReason}
              </div>
              <div className="mt-6">
                <FormButton title="Try Again" onClick={handleTryAgain} />
              </div>
            </>
          ) : (
            <>
              Unfortunately, your verification has been declined. The reason
              provided was:
              <div className="mt-4 p-4 bg-brand-gray-50 rounded-lg text-left">
                {rejectionReason}
              </div>
              <div className="mt-4">
                If you believe this is a mistake or have additional
                documentation to provide, please contact our support team.
              </div>
            </>
          )}
        </span>
        <div className="h-[1px] bg-black/5 w-full" />
        <div className="flex flex-col items-center p-5">
          <p className="font-medium mb-2">Need help?</p>
          <span className="text-description">
            Write us on{' '}
            <a
              href="mailto:support@penning.io"
              className="hover:underline text-brand-blue"
            >
              support@penning.io
            </a>
            .
          </span>
        </div>
      </div>
      <KycResidenceModal
        isOpen={isResidenceModalOpen}
        onClose={() => setIsResidenceModalOpen(false)}
        onSubmit={handleSubmitResidence}
        isLoading={isDeclaringResidence}
      />
    </PageWrapper>
  )
}

export default Cancelled
